import { useState } from "react";
import { Link } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

import {
  mfaResetRequestSchema,
  type MfaResetRequestRequest,
} from "@sendsms/shared";
import { api } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardFooter,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

export default function MfaResetRequestPage() {
  const { t } = useTranslation();
  const [topError, setTopError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const form = useForm<MfaResetRequestRequest>({
    resolver: zodResolver(mfaResetRequestSchema),
    defaultValues: { email: "" },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (body: MfaResetRequestRequest) => api.mfaResetRequest(body),
    onSuccess: () => setSent(true),
    onError: (err: unknown) => {
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  const emailError = form.formState.errors.email?.message
    ? translateFieldError(t, form.formState.errors.email.message)
    : undefined;

  if (sent) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
            <CheckCircle2 className="h-6 w-6 text-success-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.request_sent_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_reset.request_sent_message")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-caption font-medium text-brand-500 hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("auth.forgot_password.back_to_login")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.mfa_reset.request_title")}</CardTitle>
        <CardSubtitle>{t("auth.mfa_reset.request_subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        <Alert
          tone="warning"
          icon={<AlertTriangle className="h-5 w-5" />}
          description={t("auth.mfa_reset.request_warning")}
          className="mb-5"
        />

        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            mutation.mutate(data);
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.mfa_reset.request_email_label")}
            type="email"
            autoComplete="email"
            autoFocus
            required
            {...form.register("email")}
            error={emailError}
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={mutation.isPending}
          >
            {t("auth.mfa_reset.request_submit")}
          </Button>
        </form>
      </CardBody>

      <CardFooter>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-caption font-medium text-brand-500 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("auth.forgot_password.back_to_login")}
        </Link>
      </CardFooter>
    </Card>
  );
}
